import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { setFormArrayLength } from '@core/utils/set-form-array-length';
import {
  travelerSelectorInitialState,
  TravelerSelectorState,
  TravelerSelectorViewModel,
} from '@dynamic-components/booker-v2/shared/traveler-selector/traveler-selector.state';
import {
  SelectedTravelers,
  TravelerAllowedRange,
  TravelerGroup,
  TravelerRuleWarning,
  TravelerSelectorOptions,
} from '@dynamic-components/booker-v2/shared/traveler-selector/types';
import {
  getTravelerAllowedRange,
  getTravelersSummary,
  shouldWarnTravelerRule,
  toSelectedTravelers,
  travelersExceedAllowedRange,
  updateTravelerGroupsWithinAllowedRange,
} from '@dynamic-components/booker-v2/shared/traveler-selector/utils/traveler-selector.util';
import { ComponentStore } from '@ngrx/component-store';
import { BOOKER_CONFIG, BookerType, TravelerSelector } from '@shared/booker';
import { AnalyticsFacade } from '@store/analytics/analytics.facade';
import { JbFlyoutPlacementEnum } from 'jb-component-library';
import { equals } from 'ramda';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pluck,
  share,
  skip,
  skipUntil,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

const MAX_PASSENGER_NUMBER = BOOKER_CONFIG.PAX.GUEST_LIMIT;
@Component({
  selector: 'dot-traveler-selector',
  templateUrl: './traveler-selector.component.html',
  styleUrls: ['./traveler-selector.component.scss'],
  providers: [
    ComponentStore,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TravelerSelectorComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: TravelerSelectorComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TravelerSelectorComponent
  implements ControlValueAccessor, OnDestroy, AfterViewInit, OnInit
{
  @Input() set bookerType(bookerType: BookerType) {
    this.store.patchState({ bookerType });
  }

  @Input() set content(content: TravelerSelector) {
    this.store.patchState({ content });
  }

  @Input() set infantsCountTowardsTotalTravelers(
    infantsCountTowardsTotalTravelers: boolean,
  ) {
    this.store.patchState({ infantsCountTowardsTotalTravelers });
  }

  @Input() set placement(placement: JbFlyoutPlacementEnum) {
    this.store.patchState({ placement });
  }

  @Input() set flyOutToFitInput(flyOutToFitInput: boolean) {
    this.store.patchState({ flyOutToFitInput });
  }
  @Input() set travelerCountError(travelerCountError: string) {
    this.store.patchState({ travelerCountError });
  }

  @Input() set travelerSelectorId(travelerSelectorId: string) {
    this.store.patchState({ travelerSelectorId });
  }

  @Input() set showValidationErrors(showValidationErrors: boolean) {
    this.store.patchState({ showValidationErrors });
  }

  @Input() set defaultToOneTravellerIfZeroSet(
    defaultToOneTravellerIfZeroSet: boolean,
  ) {
    this.store.patchState({
      defaultToOneTravellerIfZeroSet: !!defaultToOneTravellerIfZeroSet,
    });
  }

  @Input() bookerId: string;

  @ViewChild('jbSelectButton') selectButtonComponent: any;

  readonly roomsControl = this.fb.control<number>(1);

  readonly rooms$ = this.roomsControl.valueChanges.pipe(
    startWith(this.roomsControl.value),
  );

  readonly travelerGroupsControl = this.fb.array<TravelerGroup>([
    {
      adults: 1,
      children: 0,
      infants: 0,
      childAges: [],
    },
  ]);

  readonly formValue$: Observable<TravelerGroup[]> =
    this.travelerGroupsControl.valueChanges.pipe(
      startWith(this.travelerGroupsControl.value),
      distinctUntilChanged((a, b) => equals(a, b)),
      share(),
    );

  readonly infantsCountTowardsTotalTravelers$ = this.store.select(
    state => state.infantsCountTowardsTotalTravelers,
  );

  readonly options$: Observable<TravelerSelectorOptions> = this.store
    .select(state => state.bookerType)
    .pipe(
      map(bookerType =>
        // eslint-disable-next-line no-nested-ternary
        bookerType === BookerType.air
          ? {
              multipleGroups: false,
              allowUnaccompaniedMinors: true,
              requireChildAges: false,
            }
          : bookerType === BookerType.vacations
          ? {
              multipleGroups: true,
              allowUnaccompaniedMinors: false,
              requireChildAges: true,
            }
          : {
              multipleGroups: false,
              allowUnaccompaniedMinors: false,
              requireChildAges: true,
            },
      ),
    );

  /** Selected Travelers before allowed ranges have been applied */
  readonly selectedTravelers$: Observable<SelectedTravelers> =
    this.formValue$.pipe(
      map(toSelectedTravelers),
      startWith({
        allTravelers: { adults: 1, children: 0, infants: 0, childAges: [] },
        groups: [
          {
            adults: 1,
            children: 0,
            infants: 0,
            childAges: [],
          },
        ],
      }),
    );

  /** Selected travelers that has been processed to be within allowed ranges */
  readonly processedSelectedTravelers$: Observable<SelectedTravelers> =
    combineLatest([
      // ignore default value
      // debounceTime to avoid infinite loop, see DOT-9749
      this.selectedTravelers$.pipe(skip(1), debounceTime(10)), // ignore default value
      this.options$,
      this.infantsCountTowardsTotalTravelers$,
    ]).pipe(
      switchMap(([selectedTravelers, options, infants]) => {
        const allowedRange = getTravelerAllowedRange(
          selectedTravelers,
          infants,
          options.allowUnaccompaniedMinors,
        );
        const isExceeded = travelersExceedAllowedRange(
          selectedTravelers,
          allowedRange,
        );
        let updated = selectedTravelers;
        // if one of the travelers group has been exceeded get new Selected Travelers group
        if (isExceeded) {
          const group = updateTravelerGroupsWithinAllowedRange(
            selectedTravelers,
            allowedRange,
          );
          updated = toSelectedTravelers(group);
        }
        return of(updated);
      }),
    );

  readonly allTravelers$ = this.selectedTravelers$.pipe(
    map(({ allTravelers }) => allTravelers),
  );

  readonly content$: Observable<TravelerSelector> = this.store
    .select(state => state.content)
    .pipe(filter(content => !!content));

  readonly travelersSummary$: Observable<{
    compact: string;
    full: string;
  }> = combineLatest([
    this.allTravelers$,
    this.store
      .select(state => state.content)
      .pipe(
        filter(content => !!content),
        take(1),
      ),
    this.rooms$.pipe(
      skipUntil(this.options$.pipe(pluck('multipleGroups'), filter(Boolean))),
      startWith(null),
    ),
  ]).pipe(
    map(([allTravelers, content, rooms]) => {
      return {
        full: getTravelersSummary(allTravelers, content, false),
        compact: getTravelersSummary(allTravelers, content, true, rooms),
      };
    }),
  );

  readonly travelerAllowedRange$: Observable<TravelerAllowedRange[]> =
    this.store.select(
      this.selectedTravelers$,
      this.options$,
      this.infantsCountTowardsTotalTravelers$,
      (selectedTravelers, options, infantsCountTowardsTotalTravelers) =>
        getTravelerAllowedRange(
          selectedTravelers,
          infantsCountTowardsTotalTravelers,
          options.allowUnaccompaniedMinors,
        ),
    );

  readonly maxGroups$ = combineLatest([
    this.options$,
    this.allTravelers$,
    this.infantsCountTowardsTotalTravelers$,
    this.rooms$,
  ]).pipe(
    map(([options, allTravelers, infantsCountTowardsTotalTravelers, rooms]) =>
      options.multipleGroups
        ? rooms +
          (BOOKER_CONFIG.PAX.ROOM_LIMIT -
            (allTravelers.adults +
              allTravelers.children +
              (infantsCountTowardsTotalTravelers ? allTravelers.infants : 0)))
        : 1,
    ),
  );

  readonly rules$: Observable<TravelerRuleWarning[]> = this.store.select(
    this.content$,
    this.selectedTravelers$,
    this.store.select(state => state.bookerType),
    this.infantsCountTowardsTotalTravelers$,
    (
      content,
      selectedTravelers,
      bookerType,
      infantsCountTowardsTotalTravelers,
    ) =>
      (bookerType === BookerType.air ? content.rules : content.vacation_rules)
        .map(rule => ({
          ...rule,
          ...(rule.id === `MAX-TRAVELERS`
            ? {
                copy: rule.copy.replace(
                  /\.?$/,
                  `${
                    infantsCountTowardsTotalTravelers
                      ? ''
                      : ' not including infants'
                  }.`,
                ),
              }
            : {}),
          warn: shouldWarnTravelerRule(
            rule.id,
            selectedTravelers,
            infantsCountTowardsTotalTravelers,
          ),
        }))
        .filter(rule => rule.warn !== null),
  );

  readonly vm$: Observable<TravelerSelectorViewModel> = this.store.select(
    this.store.state$,
    this.options$,
    this.travelersSummary$,
    this.travelerAllowedRange$,
    this.rules$,
    this.maxGroups$,
    (
      state,
      options,
      travelersSummary,
      travelerAllowedRange,
      rules,
      maxGroups,
    ) => ({
      ...state,
      options,
      travelersSummary,
      travelerAllowedRange,
      rules,
      maxGroups,
    }),
  );

  readonly writeValue = this.store.effect<SelectedTravelers>(value$ =>
    value$.pipe(
      withLatestFrom(this.options$),
      tap(([value, options]) => {
        let groups;
        if (options.multipleGroups) {
          groups =
            value.groups.length > BOOKER_CONFIG.PAX.ROOM_LIMIT
              ? value.groups.slice(0, BOOKER_CONFIG.PAX.ROOM_LIMIT)
              : value.groups;
        } else {
          // single group only
          groups = value.groups.slice(0, 1);
        }
        this.roomsControl.setValue(groups.length);
        this.travelerGroupsControl.setValue(groups);
      }),
    ),
  );

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private store: ComponentStore<TravelerSelectorState>,
    private changeDetectorRef: ChangeDetectorRef,
    private analyticsFacade: AnalyticsFacade,
  ) {
    this.store.setState(travelerSelectorInitialState);
    this.subscriptions.add(
      combineLatest([
        this.content$,
        this.infantsCountTowardsTotalTravelers$,
      ]).subscribe(([content, infantsCountTowardsTotalTravelers]) => {
        this.travelerGroupsControl.setValidators([
          this.travelerCountValidator(content.errors.noPassengerError),
          this.maxPassengerValidator(
            content.errors.maxPassengerError,
            infantsCountTowardsTotalTravelers,
          ),
          this.childAgeValidator(content.errors.invalidChildAge),
        ]);
        this.travelerGroupsControl.updateValueAndValidity();
      }),
    );

    this.subscriptions.add(
      this.roomsControl.valueChanges
        .pipe(withLatestFrom(this.options$))
        .subscribe(([rooms, { allowUnaccompaniedMinors }]) =>
          this.setTravelerGroupFormArrayLength(rooms, allowUnaccompaniedMinors),
        ),
    );
  }

  public focusOnTravellerSelector() {
    this.selectButtonComponent.anchor.open();
  }

  setTravelerGroupFormArrayLength(groupCount, allowUnaccompaniedMinors) {
    setFormArrayLength(groupCount, this.travelerGroupsControl, () =>
      this.fb.control<TravelerGroup>({
        adults: allowUnaccompaniedMinors ? 0 : 1,
        children: 0,
        infants: 0,
        childAges: [],
      }),
    );
  }

  registerOnChange(fn: (value: SelectedTravelers) => void): void {
    this.subscriptions.add(this.processedSelectedTravelers$.subscribe(fn));
  }

  registerOnTouched(fn: any): void {
    return;
  }

  maxPassengerValidator(
    message: string,
    infantsCountTowardsTotalTravelers?: boolean,
  ): ValidatorFn {
    return ({
      value,
    }: FormControl<TravelerGroup[]>): { [key: string]: string } => {
      const { allTravelers } = toSelectedTravelers(value);
      const tooMany = infantsCountTowardsTotalTravelers
        ? allTravelers.adults + allTravelers.children + allTravelers.infants >
          MAX_PASSENGER_NUMBER
        : allTravelers.adults + allTravelers.children > MAX_PASSENGER_NUMBER;

      return tooMany ? { maxPassengerError: message } : null;
    };
  }

  travelerCountValidator(message: string): ValidatorFn {
    return ({
      value,
    }: FormControl<TravelerGroup[]>): { [key: string]: string } => {
      const { allTravelers } = toSelectedTravelers(value);
      return allTravelers.adults +
        allTravelers.children +
        allTravelers.infants ===
        0
        ? { travelerCountError: message }
        : null;
    };
  }

  childAgeValidator(message: string): ValidatorFn {
    return ({
      value,
    }: FormControl<TravelerGroup[]>): { [key: string]: string } => {
      const { allTravelers } = toSelectedTravelers(value);
      return allTravelers.childAges.includes(null)
        ? { childAgeError: message }
        : null;
    };
  }

  validate(formControl: FormControl<SelectedTravelers>): ValidationErrors {
    return this.travelerGroupsControl.errors;
  }

  ngOnInit() {
    // workaround to the issue with the vm$ not allowing for proper updates in template todo: remove vm
    this.subscriptions.add(
      this.vm$.pipe(startWith(true)).subscribe(() => {
        this.changeDetectorRef.detectChanges();
      }),
    );
    /*
     Wait until the vm$ is settled and then send a analytics complete event
     Bottleneck async call for booker hotel + points
    */
    this.subscriptions.add(
      this.vm$.pipe(skip(1), debounceTime(1000), take(1)).subscribe(() => {
        if (this.bookerId.startsWith(BookerType.hotelPlusPoints)) {
          this.analyticsFacade.bookerLoadDoneEventCall(this.bookerId);
        }
      }),
    );
  }

  ngAfterViewInit() {
    this.subscriptions.add(
      this.selectButtonComponent?.flyout.afterClosed
        .pipe(
          withLatestFrom(
            this.store.select(state => state.defaultToOneTravellerIfZeroSet),
          ),
        )
        .subscribe(([_, defaultToOneTravellerIfZeroSet]) => {
          const travelerGroupsControlValue =
            this.travelerGroupsControl.controls[0].value;
          const totalTravelers =
            travelerGroupsControlValue.adults +
            travelerGroupsControlValue.children +
            travelerGroupsControlValue.infants;
          if (defaultToOneTravellerIfZeroSet && totalTravelers === 0) {
            // We don't need to check options.multipleGroups=false. Only time it is true is for vacations
            // booker and we don't set defaultToOneTravellerIfZeroSet=true for vacations booker.
            this.travelerGroupsControl.controls[0].setValue({
              ...this.travelerGroupsControl.controls[0].value,
              adults: 1,
            });
          }
          this.changeDetectorRef.detectChanges();
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
